import { PouchDoc } from './pouch';

/** Doc id of the day-task-status cache singleton (one per user DB copy). */
export const DAY_TASK_STATUS_CACHE_ID = 'day-task-status-cache';

/**
 * Status of one task category (or a whole day) on the calendar day markers.
 * Lives here (not in the Angular component) so plain data-model code and unit
 * tests can use it without pulling in Angular (#4212).
 */
export enum TaskStatus {
  Completed = 'completed',
  Incomplete = 'incomplete',
  Future = 'future',
  NoTask = 'no_task',
  InProgress = 'in_progress'
}

/**
 * Individual task statuses for different categories.
 */
export interface DayTaskStatuses {
  workoutStatus: TaskStatus;
  mealsStatus: TaskStatus;
  weighInStatus: TaskStatus;
  weeklyCheckupStatus: TaskStatus;

  /**
   * The LOCAL calendar date (`YYYY-MM-DD`) these statuses were worked out on
   * (#4694).
   *
   * A day's statuses are only final once the day is over. Worked out while the
   * day is still running, they describe a day in progress — the entries are
   * still `Planned`, which comes out as `incomplete` — and the user finishes
   * them later. That value is plausible for a past day, so nothing ever looked
   * at it again and the calendar dot stayed wrong for ever. This field is what
   * makes "worked out before the day ended" tellable from "worked out after",
   * and `mustRecomputeCachedStatus` re-checks the first kind exactly once.
   *
   * Optional for two separate reasons, and both must stay true:
   * - the in-memory display memo holds `DayTaskStatuses` too and deliberately
   *   carries NO stamp, so that the stamp can never reach the memo's
   *   `deepCompareEQ` change test and make an unchanged day look changed;
   * - a document written by an older app version has no stamp at all.
   * A missing stamp therefore means "do not re-check", never "re-check".
   */
  computedOn?: string;
}

/**
 * Document that caches daily task statuses for quick calendar rendering.
 * This avoids having to query all log entries repeatedly.
 */
export interface DayTaskStatusCache extends PouchDoc {
  /**
   * Map of date (YYYY-MM-DD) to task statuses for each category.
   * Stored as object for CouchDB compatibility.
   *
   * `readonly` on purpose (df-827a7f3bf41e). Every stored day must carry a
   * `computedOn` stamp, and the only place that applies it is
   * `DayTaskStatusCacheDoc.putDay` in the client. Making the map read-only here is
   * what turns "each writer remembered the stamp" into a compile-time guarantee
   * instead of a regular expression scanning the service's source text. Reading a
   * day through this field is fine; to store or remove one, go through that class.
   */
  readonly statusesByDate: { readonly [date: string]: DayTaskStatuses };

  /**
   * Last time this cache was fully rebuilt.
   * Used to determine if a rebuild is needed.
   */
  lastRebuild_utc?: string;

  /**
   * Version number for cache format.
   * Increment this when changing the cache structure.
   */
  version: number;
}